// src/components/Sidebar/SidebarHowTo.tsx
"use client";
import { ChevronDown } from "lucide-react";

import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useEventLogger } from "@/lib/useEventLogger";
import { useRecordContext } from "@/contexts/RecordContext";
import { useHowtoCanPost } from "@/hooks/useHowto";
import type { UserProfile } from "@/types/UserProfile";
import type { SelectedHowToStep } from "@/contexts/RecordContext";

function assertUser(
  user: UserProfile | null | undefined
): asserts user is UserProfile {
  if (!user) {
    throw new Error("userProfile is required here");
  }
}

type SidebarHowToProps = {
  openSection: string | null;
  setOpenSection: (v: string | null) => void;
  disabled?: boolean;
};

export default function SidebarHowTo({
  openSection,
  setOpenSection,
  disabled = false,
}: SidebarHowToProps) {
  
  const recordContext = useRecordContext();

  if (!recordContext || recordContext.userProfileLoading || !recordContext.userProfile) {
    return <div style={{ color: "red" }}>ユーザープロファイル未取得中...</div>;
  }

  const selectedHowToStep = recordContext.selectedHowToStep;
  const setSelectedHowToStep = recordContext.setSelectedHowToStep;

  const userName = recordContext.userProfile.displayName ?? "ゲスト";

  /**
   * 重要：
   * RecordContext の howToList を「公開」と「mine」で使い回すと、
   * タブ切替のたびに配列が上書きされて表示が逆転/消失する。
   * → SidebarHowTo 内でローカルに分離する。
   */
  const [publicHowtos, setPublicHowtos] = useState<any[]>([]);
  const [myHowtos, setMyHowtos] = useState<any[]>([]);

  // 型安全のため一度 uid を確定させる
  const currentUserId = recordContext.userProfile.uid;

  const normalizeHowToStep = (step: any, stepKey: string): SelectedHowToStep => {
  if (typeof step === "string") {
    return {
      key: stepKey,
      text: step,
    };
  }

  return {
    key: stepKey,
    text: typeof step?.text === "string" ? step.text : "",
    action: step?.action,
    autoRun: step?.autoRun,
    ctaLabel: step?.ctaLabel,
  };
};

  // マイガイド：createdAt の新しい順に並べる（UI側で保証）
  const myHowToList = [...myHowtos].sort((a, b) => {
    const toMs = (v: any) => {
      if (!v) return 0;
      if (typeof v?.toDate === "function") return v.toDate().getTime(); // Firestore Timestamp
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : 0;
    };
  
    const diff = toMs(b.createdAt) - toMs(a.createdAt);
    if (diff !== 0) return diff;
  
    // createdAt が同値 / 無効な場合は id で安定ソート
    return String(b.id).localeCompare(String(a.id));
  });

  const [activeTab, setActiveTab] = useState<"list" | "mine" | "register">("list");
  const [selectedHowToId, setSelectedHowToId] = useState<string | null>(null);
  const [newGuideName, setNewGuideName] = useState("");
  const [newGuideSteps, setNewGuideSteps] = useState([{ id: uuidv4(), text: "" }]);

  useEffect(() => {
    if (activeTab !== "list") return;

    const fetchHowTos = async () => {
      try {
        const res = await fetch("/api/howto", { cache: "no-store" });
        if (!res.ok) throw new Error("HowTo取得に失敗");
        const howtos = await res.json();
        setPublicHowtos(Array.isArray(howtos) ? howtos : []);
      } catch (err) {
        setPublicHowtos([]);
        console.error("HowToガイドの取得エラー", err);
      }
    };

    fetchHowTos();
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!publicHowtos.length) return;

    const seen = localStorage.getItem("kataruta_step_seen");
    if (seen) return;

    const howto = publicHowtos.find((h) => h?.name === "はじめての3枚語り");
    if (!howto || !Array.isArray(howto.steps) || !howto.steps[0]) return;

    const firstStep = normalizeHowToStep(howto.steps[0], `${howto.id}-0`);
    if (!firstStep.text) return;

    setOpenSection("howto");
    setActiveTab("list");
    setSelectedHowToId(howto.id);
    setSelectedHowToStep(firstStep);

    localStorage.setItem("kataruta_step_seen", "1");
  }, [publicHowtos, setOpenSection, setSelectedHowToStep]);

  // マイガイドタブ専用
  useEffect(() => {
    if (activeTab !== "mine") return;

    const fetchMine = async () => {
      try {
        const res = await fetch("/api/howto?mine=true", { cache: "no-store" });
        if (!res.ok) throw new Error("マイガイド取得失敗");
        const howtos = await res.json();
        setMyHowtos(howtos);
      } catch (err) {
        console.error("マイガイド取得エラー", err);
        setMyHowtos([]);
      }
    };

    fetchMine();
  }, [activeTab]);

  const handleRegister = async (authorNameOverride?: string) => {
    if (!newGuideName.trim() || newGuideSteps.length === 0) return;

    const authorName =
      authorNameOverride ??
      recordContext.userProfile?.displayName ??
      tempDisplayName ??
      "";

    if (!authorName.trim()) {
      setShowNameModal(true);
      return;
    }

    try {
      const res = await fetch("/api/howto", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newGuideName.trim(),
          steps: newGuideSteps.map((s) => s.text).filter(Boolean),
          authorName,
        }),
      });

      if (!res.ok) throw new Error("登録に失敗しました");

      logMyguideRegistered(); // Pro専用：マイガイド登録イベント

      setNewGuideName("");
      setNewGuideSteps([{ id: uuidv4(), text: "" }]);
      setActiveTab("mine");

      const mineRes = await fetch("/api/howto?mine=true", { cache: "no-store" });
      if (!mineRes.ok) throw new Error("マイガイド再取得に失敗");
      const mineHowtos = await mineRes.json();
      setMyHowtos(mineHowtos);

    } catch (err) {
      alert("登録エラー");
      console.error(err);
    }
  };

  const isOpen = openSection === "howto";
  // ===== UI state derived from postStatus =====
  const user = recordContext.userProfile;
  if (!user) {
    // ログイン前 or 読み込み中
    return null;
  }

  const postStatus = useHowtoCanPost(user);
  const canPost = postStatus === "allowed";
  const hasDisplayName = !!recordContext.userProfile.displayName?.trim();
  const canSubmit = canPost && hasDisplayName;

  const [showNameModal, setShowNameModal] = useState(false);
  const [tempDisplayName, setTempDisplayName] = useState("");

  const { log } = useEventLogger();
  // 2026-07-23修正：以前はbooleanフラグ1個で「この画面が表示されてから最初の1回だけ」しか
  // 送信しておらず、同じ画面内で複数の異なるhowtoを試しても2件目以降が記録されなかった
  // （Kazuto実機確認で発覚）。howtoIdごとの重複送信だけを防ぐSetに変更し、
  // 違うhowtoを試すたびにちゃんと記録されるようにする。
  const loggedHowtoIdsRef = useRef<Set<string>>(new Set());

  // howto_used: ガイドのステップをセッションに適用したとき（同一howtoIdにつき1画面表示で1回のみ）
  // howtoId（docID）を主キーとして記録。howtoName はデバッグ用に併記。
  const logHowtoUsed = (howtoId?: string, howtoName?: string) => {
    // howtoIdが無いイベント（旧ログ等）は従来通り毎回スキップせず1回だけ送る特殊ケースとして
    // 空文字キーで扱う（howtoId無しは元々の実装でもほぼ発生しない想定）。
    const dedupeKey = howtoId ?? "";
    if (!loggedHowtoIdsRef.current.has(dedupeKey)) {
      loggedHowtoIdsRef.current.add(dedupeKey);
      log("howto_used", howtoId ? { howtoId, howtoName } : {});
    }
  };

  // myguide_registered: マイガイドを新規登録したとき（Pro専用機能）
  const logMyguideRegistered = () => log("myguide_registered");

  const handleToggle = () => {
    setOpenSection(isOpen ? null : "howto");
  };

  // 登録タブは常に開ける。submit の可否は canPost のみで制御

  return (
    <nav className={`border-t pt-4 mt-6 ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
      <h2
        className="text-sm font-bold mb-2 flex justify-between items-center transition text-gray-700 cursor-pointer"
        onClick={handleToggle}
      >
        使い方ガイド
        <ChevronDown size={14} className={isOpen ? "rotate-180 transition-transform" : "transition-transform"} />
      </h2>
      {isOpen && (
        <>
          <div className="flex gap-2 mb-2 border-b">
          {["list", "mine", "register"].map((tab) => {
    const isActive = activeTab === tab;

    return (
      <button
        key={tab}
        onClick={() => setActiveTab(tab as any)}
        className={`
          pb-1 px-2 text-sm font-medium border-b-2
          ${
            isActive
              ? "border-black text-black"
              : "border-transparent text-gray-500 hover:text-black hover:border-gray-300"
          }
        `}
      >
        {tab === "list"
          ? "ガイド"
          : tab === "mine"
          ? "マイガイド"
          : "登録"}
      </button>
    );
  })}
</div>
          <div className="bg-gray-50 p-2 rounded-md space-y-2 max-h-60 overflow-y-auto">
          {activeTab === "register" ? (
  <div className="space-y-2">
    <p className="text-xs text-gray-500">
      登録したガイドは、まずは自分だけが見られる「マイガイド」に保存されます（下書きとして自由に使えます）。
    </p>
    <input
      type="text"
      placeholder="タイトル"
      value={newGuideName}
      onChange={(e) => setNewGuideName(e.target.value)}
      className="w-full border rounded px-2 py-1 text-sm"
      disabled={!canSubmit}
    />

    {newGuideSteps.map((step, i) => (
      <textarea
        key={step.id}
        value={step.text}
        onChange={(e) => {
          const updated = newGuideSteps.map((s) =>
            s.id === step.id ? { ...s, text: e.target.value } : s
          );
          setNewGuideSteps(updated);
        }}
        placeholder="ステップ"
        className="w-full border rounded px-2 py-1 text-sm"
        disabled={!canSubmit}
      />
    ))}

    <div className="flex flex-col items-center space-y-2 mt-2">
      <button
        onClick={() =>
          setNewGuideSteps([...newGuideSteps, { id: uuidv4(), text: "" }])
        }
        className="text-blue-600 text-sm hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={!canSubmit}
      >
        ＋ ステップを追加
      </button>

      <span className="inline-block">
      <button
        onClick={async () => {
          if (!hasDisplayName) {
            setShowNameModal(true);
            return;
          }
          if (!canPost) return;
          await handleRegister();
        }}
        disabled={!canSubmit}
        className={`
          px-4 py-2 rounded text-sm font-medium
          ${canSubmit
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"}
        `}
      >
        登録する
      </button>

      {!canPost && (
         <p className="mt-2 text-xs text-gray-500">
           この機能は、現在のご利用内容ではお使いいただけません。
         </p>
      )}
      </span>
    </div>
  </div>
          ) : activeTab === "mine" ? (
            myHowToList.length === 0 ? (
              <p className="text-gray-400">まだガイドを登録していません。</p>
            ) : (
              myHowToList.map((howto) => (
                <div key={howto.id} className="border rounded-md overflow-hidden">
                  <button
                    onClick={() => {
                      const isClosing = selectedHowToId === howto.id;
                      setSelectedHowToId(isClosing ? null : howto.id);

                      if (isClosing && selectedHowToStep?.key?.startsWith(`${howto.id}-`)) {
                        setSelectedHowToStep(null);
                    }
                  }}
                    className={`block w-full text-left p-2 text-sm ${
                      selectedHowToId === howto.id
                        ? "bg-blue-50 border-l-4 border-blue-400"
                        : "bg-white"
                    } hover:bg-gray-100`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{howto.name}</span>
                      {(() => {
                        const rawAuthor =
                        (howto as any).authorName ??
                        (howto as any).author_display_name ??
                        "";
                      
                      const displayAuthor =
                        rawAuthor && !rawAuthor.includes("@") ? rawAuthor : "";

                        return displayAuthor ? (
                          <span className="text-xs text-gray-500 ml-2">
                            {displayAuthor}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </button>
          
                  {selectedHowToId === howto.id && (
                    <div className="px-4 pb-2 text-sm text-gray-700 space-y-1 max-h-52 overflow-y-auto">
                      {howto.steps.map((step: any, i: number) => {
                        const stepKey = `${howto.id}-${i}`;
                        const normalizedStep = normalizeHowToStep(step, stepKey);
                        const isSelected = selectedHowToStep?.key === stepKey;

                        return (
                          <div
                            key={stepKey}
                            onClick={() => {
                              const newValue = isSelected ? null : normalizedStep;
                              setSelectedHowToStep(newValue);
                              if (newValue) logHowtoUsed(howto.id, howto.name);
                            }}
                            className={`cursor-pointer px-3 py-1 rounded border transition ${
                              isSelected
                                ? "bg-blue-100 border-blue-400 font-semibold text-blue-900"
                                : "bg-white border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            {i + 1}. {normalizedStep.text}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )
          ) : publicHowtos.length === 0 ? (
            <p className="text-gray-400">登録された使い方がありません。</p>
          ) : (
            publicHowtos.map((howto) => (
                <div key={howto.id} className="border rounded-md overflow-hidden">
                  <button
                    onClick={() => {
                      const isClosing = selectedHowToId === howto.id;
                      setSelectedHowToId(isClosing ? null : howto.id);

                      if (isClosing && selectedHowToStep?.key?.startsWith(`${howto.id}-`)) {
                        setSelectedHowToStep(null);
                      }
                    }}
                    className={`block w-full text-left p-2 text-sm ${
                      selectedHowToId === howto.id
                        ? "bg-blue-50 border-l-4 border-blue-400"
                        : "bg-white"
                    } hover:bg-gray-100`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{howto.name}</span>
                      {(() => {
                        const rawAuthor =
                        (howto as any).authorName ??
                        (howto as any).author_display_name ??
                        "";
                      
                      const displayAuthor =
                        rawAuthor && !rawAuthor.includes("@") ? rawAuthor : "";

                        return displayAuthor ? (
                          <span className="text-xs text-gray-500 ml-2">{displayAuthor}</span>
                        ) : null;
                      })()}
                    </div>
                  </button>
                  {selectedHowToId === howto.id && (
                    <div className="px-4 pb-2 text-sm text-gray-700 space-y-1 max-h-52 overflow-y-auto">
                      {howto.steps.map((step: any, i: number) => {
                        const stepKey = `${howto.id}-${i}`;
                        const normalizedStep = normalizeHowToStep(step, stepKey);
                        const isSelected = selectedHowToStep?.key === stepKey;

                        return (
                          <div
                            key={stepKey}
                            onClick={() => {
                              const newValue = isSelected ? null : normalizedStep;
                              setSelectedHowToStep(newValue);
                              if (newValue) logHowtoUsed(howto.id, howto.name);
                            }}
                            className={`cursor-pointer px-3 py-1 rounded border transition ${
                              isSelected
                                ? "bg-blue-100 border-blue-400 font-semibold text-blue-900"
                                : "bg-white border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            {i + 1}. {normalizedStep.text}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
      {showNameModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">

            <h2 className="text-lg font-bold mb-3">
              ガイドを登録するには表示名が必要です。
            </h2>

            <input
              type="text"
              value={tempDisplayName}
              onChange={(e) => setTempDisplayName(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mb-4"
              placeholder="表示名を入力してください"
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowNameModal(false)}
                className="px-4 py-2 rounded-lg bg-gray-200"
              >
                キャンセル
              </button>

              <button
                onClick={async () => {
                  if (!tempDisplayName.trim()) return;

                  await fetch("/api/user-profile", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      displayName: tempDisplayName.trim(),
                    }),
                  });

                  const newName = tempDisplayName.trim();

                  await recordContext.fetchUserProfile();

                  setShowNameModal(false);
                  setTempDisplayName("");

                  // 🔥 override付きで直接登録
                  await handleRegister(newName);
                }}
                className="px-4 py-2 rounded-lg bg-green-600 text-white"
              >
                保存して続ける
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
